import { RtoRiskType } from '../types/orders'
import { discountConstants, orderTableConstants, giftCardRowLabel } from './constants'
import { getDateTime, numberWithCommas, roundToDecimals } from './helpers'
import { UpsellEntitledProductType, UpsellEntitledCollectionType, UpSellTableDataType } from '../types/upsell'
import { getOfferValueAmountType } from './parsers'
import moment from 'moment'
import { OnboardingStateType, OnboardingStepType, SubscriptionV2Type } from '../types/onboardingV2'

export interface OrdersTableAdapterRow {
  data: any
  created_at: string
  source_order_id: string
  source_order: any
  fms_order: any
  is_edited: boolean
  customer_name: string
  email: string
  phone: string
  risk_level: RtoRiskType
  refund_available: boolean
  risk_analysis: any
  platform_order_id?: string
  shopDomain?: string
}

export const OrdersTableAdapter = (row: OrdersTableAdapterRow) => {
  const {
    data,
    created_at,
    source_order_id,
    source_order,
    fms_order,
    is_edited: isEdited,
    customer_name: customerName,
    email,
    phone,
    risk_level,
    refund_available,
    risk_analysis,
    platform_order_id,
    shopDomain
  } = row
  const cleanShopDomain = shopDomain?.split('.')?.[0] || data?.order_status_url?.match(/https?:\/\/([^.]+)\./)?.[1]
  const shopifyAdminOrderUrl =
    platform_order_id && cleanShopDomain
      ? `https://admin.shopify.com/store/${cleanShopDomain}/orders/${platform_order_id}`
      : data?.order_status_url
  return {
    orderId: {
      name: data?.name,
      orderStatusUrl: shopifyAdminOrderUrl
    },
    dateCreated: created_at,
    customerName: customerName,
    email: email,
    phone: phone,
    paymentMode:
      fms_order?.[0]?.metadata?.payment_context === 'SPLIT_COD'
        ? orderTableConstants.PAYMENT_MODE_SPLIT_COD
        : source_order?.order_type === 'COD'
        ? orderTableConstants.PAYMENT_MODE_COD
        : orderTableConstants.PAYMENT_MODE_PREPAID,
    totalPrice: source_order?.amount,
    paymentStatus: (() => {
      const normalizeFmsStatus = (s: string) => {
        switch (s) {
          case 'COMPLETED':
            return 'paid'
          case 'REFUNDED':
            return 'Refunded'
          case 'PARTIALLY_REFUNDED':
            return 'Partially refunded'
          default:
            return s
        }
      }
      if (fms_order?.length > 0) return normalizeFmsStatus(fms_order[0]?.status)
      if (source_order?.status === 'CANCELLED') return 'Cancelled'
      if (source_order?.metadata?.payment_mode === orderTableConstants.PAYMENT_MODE_COD) return 'pending'
      return normalizeFmsStatus(source_order?.status)
    })(),
    refunds:
      refund_available === false ||
      fms_order?.[0]?.status === 'REFUNDED' ||
      fms_order?.[0]?.status === 'PARTIALLY_REFUNDED'
        ? orderTableConstants.REFUND_STATUS_REFUNDED
        : '',
    refundAvailable: refund_available !== false,
    refundId: fms_order?.[0]?.refunds?.[0]?.pg_reference_id ?? '',
    refundedAt:
      Array.isArray(fms_order?.[0]?.refunds) && fms_order[0].refunds.length > 0
        ? fms_order[0].refunds[fms_order[0].refunds.length - 1]?.created_at
        : fms_order?.[0]?.refunds?.[0]?.created_at,
    multipleRefunds: Array.isArray(fms_order?.[0]?.refunds) && fms_order[0].refunds.length > 1,
    refundAmount: Array.isArray(fms_order?.[0]?.refunds)
      ? fms_order[0].refunds.reduce((sum: number, r: any) => sum + parseFloat(r?.amount ?? 0), 0)
      : parseFloat(fms_order?.[0]?.refunds?.amount ?? 0),
    cancellationStatus: source_order?.status === 'CANCELLED' ? orderTableConstants.CANCELLATION_STATUS_CANCELLED : '',
    cancelledAt: source_order?.status === 'CANCELLED' ? source_order?.modified_at : undefined,
    sourceOrderId: source_order_id,
    checkoutId: source_order?.checkout_id,
    isEdited: isEdited ?? false,
    tags: data?.tags ?? '',
    risk_reasons: risk_analysis,
    risk_level: risk_level,
    draftOrder: source_order?.draft_order,
    platform_order_id: platform_order_id
  }
}

/** Maps response data to the abandoned carts table field data
 * @param {any} row                 Response Data to be parsed
 * @return                          Parsed Object
 */
export const AbandonedCartAdapter = (row: any) => {
  const pricing = row?.pricing
  return {
    id: row?.uid,
    dateCreated: row?.created_at,
    placedBy: row?.customer_name,
    dropOffEvent: row?.dropped_off_event,
    phone: row?.phone_no,
    status: row?.status,
    orderValue: pricing?.total_payable,
    marketingConsent: row.marketing_consent,
    notesCount: row?.notes_count ?? 0
  }
}

export const AbandonedCartTableDrawerAdapter = (data: any) => {
  const {
    line_items,
    checkout_url,
    drop_off_event,
    customer,
    shipping_address,
    invoices,
    created_at,
    status,
    note_attribute
  } = data || {}

  const getUTMDetails = (data: any) => {
    const excludedKeys = new Set([
      'GSTIN',
      'GSTINBusinessName',
      'shopflo_checkout_url',
      'checkout_id',
      'checkout_token',
      'long_session_id',
      'gateway',
      'c2p_link',
      'c2p_expiry'
    ])
    const utmDataArray = data
      ?.filter((filterData: any) => !excludedKeys.has(filterData?.name))
      ?.map((utmData: any) => {
        return {
          label: utmData?.name,
          value: Boolean(utmData?.value?.length) ? utmData?.value : orderTableConstants.DASH
        }
      })
    return utmDataArray
  }

  const varaintDetails = line_items?.map((item: any, index: number) => {
    return {
      id: item?.id,
      itemID: item?.item_id,
      itemName: item?.product_title ?? '',
      itemPrice: item?.price,
      itemQuantity: item?.quantity,
      originalQty: item?.quantity,
      imageURL: item?.image,
      itemVariantName: item?.variant_title ?? '',
      productId: item?.product_id,
      isFreebie: item?.is_freebie ?? false
    }
  })

  const address = [
    {
      isEmpty: shipping_address?.length === 0,
      name: shipping_address?.name ?? '',
      addressLine1: shipping_address?.address1 ?? '',
      addressLine2: shipping_address?.address2 ?? '',
      city: shipping_address?.city ?? '',
      country: shipping_address?.country ?? '',
      state: shipping_address?.state ?? '',
      state_code: shipping_address?.state_code ?? '',
      pincode: shipping_address?.zip ?? '',
      country_code: shipping_address?.country_code ?? '',
      email: shipping_address?.email ?? '',
      phone: shipping_address?.phone ?? '',
      type: shipping_address?.type ?? ''
    }
  ]

  const parsedDiscountData = invoices?.discounts?.map((item: any, index: number) => {
    return {
      code: item?.coupon_metadata?.title ?? item?.discount_code,
      amount: `- \u20B9${numberWithCommas(item?.discount)}`,
      label: item?.source
    }
  })

  return {
    dateCreated: `${getDateTime(new Date(created_at))?.date} at ${getDateTime(new Date(created_at))?.time}`,
    customerName: customer?.name ?? orderTableConstants.DASH,
    customerPhone: customer?.phone ?? orderTableConstants.DASH,
    customerEmail: customer?.email ?? orderTableConstants.DASH,
    status: status ?? orderTableConstants.DASH,
    checkoutUrl: checkout_url ?? orderTableConstants.DASH,
    dropOffEvent: drop_off_event ?? orderTableConstants.DASH,
    variantDetails: varaintDetails ?? orderTableConstants.NOT_APPLICABLE,
    address: address,
    subtotal: `\u20B9${numberWithCommas(invoices?.sub_total)}`,
    discounts: parsedDiscountData ?? orderTableConstants.DASH,
    shipping:
      invoices?.shipping != null ? `\u20B9${numberWithCommas(invoices.shipping < 0 ? 0 : invoices.shipping)}` : null,
    taxes:
      invoices?.tax != null
        ? `\u20B9${numberWithCommas(invoices.tax)}${invoices?.taxes_included ? ' (included)' : ''}`
        : null,
    totalPrice: `\u20B9${numberWithCommas(invoices?.total_payable)}`,
    rewards: `- \u20B9${numberWithCommas(invoices?.rewards)}`,
    utmDetails: getUTMDetails(note_attribute),
    codCharges: `+ \u20B9${numberWithCommas(invoices?.cod ?? 0)}`
  }
}

/** Maps response data to the discounts table field data
 * @param {any} row                 Response Data to be parsed
 * @return                          Parsed Object
 */
export const discountsAdapter = (row: any, order: any) => {
  const getCouponStatus = () => {
    if (Boolean(row?.end_date) && row?.end_date < Date.now()) return 'Expired'
    if (Boolean(row?.active)) return Boolean(row?.start_date) && row?.start_date > Date.now() ? 'Scheduled' : 'Active'
    return 'Inactive'
  }
  const getDiscountRuleSummary = () => {
    return 'Something good is here.'
  }
  return {
    id: row?.code,
    code: row?.auto_applicable ? row?.title ?? row?.code ?? '' : row?.code ?? '',
    combines:
      (row?.stackable || row?.stack_config?.stack_type === 'ALL' || row?.stack_config?.stack_type === 'CUSTOM') ??
      false,
    automatic: row?.auto_applicable ?? false,
    imported: Boolean(row['tags']) && Boolean(row['tags']?.find((element: string) => element === 'SHOPIFY')),
    discountRuleSummary: getDiscountRuleSummary(),
    expires: row?.end_date,
    status: getCouponStatus(),
    isActive: row?.active ?? false,
    couponId: row?.coupon_id,
    startDate: row?.start_date,
    startDateInDanish: moment(row?.start_date).format('DD/MM/YYYY, HH:mm'),
    description: row?.description ?? '',
    discoverable: row?.discoverable ?? false,
    longDescription: row?.long_description ?? '',
    descriptionTitle: row?.header ?? '',
    title: row?.title ?? '',
    weight: row?.discoverable ? order : null,
    usage: row?.usage ?? 0,
    gmv: row?.gmv ?? 0,
    burn: row?.burn ?? 0,
    sharableLink: row?.sharable_link,
    prepaid: Boolean(row?.coupon_type === 'PREPAID'),
    prepaidType: getOfferValueAmountType(row),
    synced: row?.tags?.includes('PLATFORM'),
    aiGenerated: row?.tags?.includes('AI_GENERATED'),
    type: row?.coupon_type
  }
}

/** Maps response data to the discounts sets table field data
 * @param {any} row                 Response Data to be parsed
 * @return                          Parsed Object
 */
export const DiscountSetsApdater = (row: any) => {
  return {
    jobId: row?.job_id,
    completionPercentage: row?.completion_percentage,
    title: row?.title,
    status: row?.state,
    couponId: row?.associated_discount_code,
    couponCount: row?.total_coupons_created
  }
}

/** Maps response data to the catalogue data
 * @param {any} row                 Response Data to be parsed
 * @return                          Parsed Object
 */
export const catalogueAdapter = (row: any, catalogueType: string | undefined) => {
  const getVariants = () => {
    const variantData = row?.variants?.map((variant: any) => {
      const adaptedVariant = catalogueAdapter(variant, 'VARIANT')
      return adaptedVariant
    })
    return variantData
  }

  switch (catalogueType) {
    case discountConstants.CATALOGUE_COLLECTION:
      return {
        id: row?.collection_id,
        imageUrl: row?.collection_image?.src,
        description: row?.name,
        isVariant: false
      }
    case discountConstants.CATALOGUE_PRODUCT:
      const primaryImage = row?.images?.find((image: any) => image?.is_primary)?.src
      return {
        id: row?.product_id,
        imageUrl: primaryImage ?? row?.images?.[0]?.src,
        description: row?.name,
        isVariant: false,
        status: row?.status,
        variants: getVariants(),
        totalVariantCount: row?.variants?.length ?? 0
      }
    case discountConstants.CATALOGUE_VARIANT:
      return {
        id: row?.variant_id,
        price: row?.current_price,
        inventory: row?.inventory_quantity ?? 0,
        description: row?.name,
        isVariant: true,
        parentDetails: {
          id: row?.product?.product_id,
          name: row?.product?.description,
          imageUrl: row?.product?.images?.[0]?.src
        }
      }
  }
}

const mapGiftCardProvider = (provider: string) => {
  switch (provider) {
    case 'SHOPIFY':
      return 'Shopify'
    case 'QWIKCILVER':
    case 'QK':
      return 'Qwikcilver'
    case 'GYFTR':
      return 'GYFTR'
    default:
      return orderTableConstants.DASH
  }
}

const mapGateWay = (pgType: string) => {
  switch (pgType) {
    case 'SF_CF':
      return 'Cashfree'
    case 'CF':
      return 'Cashfree'
    case 'SF_RP':
      return 'Razorpay'
    case 'RP':
      return 'Razorpay'
    case 'PHONEPE_V2':
    case 'PHONEPE':
      return 'PhonePe'
    case 'EASEBUZZ':
      return 'Easebuzz'
    case 'PAYU':
      return 'PayU'
    case 'PAYTM':
      return 'Paytm'
    case 'CCAVENUE':
      return 'CCAvenue'
    case 'BHARATX':
      return 'BharatX'
    case 'SF_SNPM':
      return 'Snapmint'
    case 'SNPM':
      return 'Snapmint'
    case 'VELOCITY':
      return 'Velocity'
    case 'GYFTR':
      return 'Gyftr'
    case 'PLURAL':
      return 'Pine Labs'
    default:
      return orderTableConstants.DASH
  }
}

const mapPaymentMode = (mode: string, status?: string) => {
  switch (mode) {
    case 'WALLET':
      return 'Wallet'
    case 'UPI_INTENT':
      return 'UPI'
    case 'UPI_COLLECT':
      return 'UPI'
    case 'UPI':
      return 'UPI'
    case 'SNPM':
      return 'Snapmint'
    case 'VELOCITY':
      return 'Velocity'
    case 'SAVED_MODES':
      return 'Saved mode'
    case 'PAY_LATER':
      return 'Pay later'
    case 'NETBANKING':
      return 'Netbanking'
    case 'FREE':
      return 'Online payment'
    case 'EMI':
      return 'EMI'
    case 'CASHFREE':
      return 'Cashfree'
    case 'EASEBUZZ':
      return 'Easebuzz'
    case 'CARD':
      return 'Card'
    case 'PL':
      return 'Online payment'
    case 'GC':
    case 'GIFTCARD':
    case 'SHOPFLO_GC':
    case 'SHOPIFY_GC':
      return 'Gift card'
    case 'BHARATX':
      return 'BharatX'
    case 'COD':
      return 'Cash on delivery'
    case 'CRED':
      return 'CRED'
    case 'SIMPL':
    case 'SIMPL_PL':
      return 'Simpl'
    case 'PAYTM':
      return 'Paytm'
    case 'RAZORPAY':
      return 'Razorpay'
    case 'ONLINE':
    case 'PREPAID':
      return 'Online payment'
    case 'REWARDS':
    case 'REWARDS_WALLET':
      return 'Rewards'
    case 'LOYALTY':
      return 'Loyalty'
    case 'GYFTR':
      return 'Gyftr'
    case 'PAYPAL':
      return 'PayPal'
    case 'SNAPMINT':
      return 'Snapmint'
    case 'CUSTOM_PAYMENT':
      return 'Custom payment'
    case 'OFFLINE_PAYMENT':
      return 'Offline payment'
    default:
      return 'Online payment'
  }
}

const formatContactDetails = (email?: string, phone?: string) => {
  if (!email && !phone) return orderTableConstants.DASH

  const parts = []
  if (email) parts.push(email)
  if (phone) parts.push(phone)

  return parts.join('\n')
}

const mapTransactionStatus = (status: string | undefined): string => {
  switch (status) {
    case 'COMPLETED':
      return 'Successful'
    case 'FAILED':
      return 'Failed'
    case 'CANCELLED':
      return 'Cancelled'
    case 'REFUNDED':
      return 'REFUNDED'
    case 'PARTIALLY_REFUNDED':
      return 'PARTIALLY_REFUNDED'
    default:
      return status ?? orderTableConstants.DASH
  }
}

/**
 * `order_information.amount` is a frozen snapshot from order creation and never gets
 * recalculated when an item is added to the order post-facto from the merchant dashboard —
 * only `platform_orders.data.total_outstanding` reflects that change. Grand Total intentionally
 * stays as `amount` (the original order value); this computes the "Edited Order Total" shown
 * alongside it once an order has been edited.
 *
 * `amount - total_outstanding` looks like it'd recover the already-collected prepaid leg for
 * SPLIT_COD, but it doesn't survive an edit: `amount` is frozen while `total_outstanding` grows
 * with every added item, so that subtraction silently cancels back down to `amount` after any
 * edit. The prepaid leg has to come from an immutable source instead — the actually-completed,
 * non-COD FMS transaction(s) — then the live `total_outstanding` is added on top. This same
 * formula naturally covers all three payment modes:
 * - Prepaid/online: fully collected upfront, so paidUpfront ≈ amount pre-edit.
 * - COD: nothing is ever collected upfront (paidUpfront = 0), so the total is total_outstanding alone.
 * - SPLIT_COD: paidUpfront is the fixed prepaid leg; total_outstanding is the live COD-due leg.
 */
export const calculateOrderTotal = (orderInfo: any, platformOrderData: any, fmsOrders: any) => {
  const amount = parseFloat(orderInfo?.amount) || 0
  const outstandingRaw = platformOrderData?.total_outstanding
  if (typeof outstandingRaw === 'undefined') return amount
  const outstanding = parseFloat(outstandingRaw) || 0

  const paidUpfront = (fmsOrders ?? []).reduce((sum: number, fmsOrder: any) => {
    const wasCollectedUpfront = fmsOrder?.transactions?.some(
      (transaction: any) => transaction?.status === 'COMPLETED' && transaction?.payment_mode !== 'COD'
    )
    return wasCollectedUpfront ? sum + (parseFloat(fmsOrder?.amount) || 0) : sum
  }, 0)

  return paidUpfront + outstanding
}

export const orderTableDrawerAdapter = (data: any) => {
  const checkoutInfo = data?.checkout_information
  const fmsOrders = data?.fms_orders
  const refundInfo = data?.refund_information
  const settlementInfo = data?.settlement_information
  const platformOrder = data?.platform_orders
  const customer = platformOrder?.data?.customer
  const c2pLink = checkoutInfo?.metadata?.c2p_link?.enabled ? checkoutInfo?.metadata?.c2p_link?.checkout_url : null
  const orderInfo = data?.order_information
  const lineItems = data?.line_items

  const successfulTransaction: any = {}
  const transactionHistory: any[] = []
  let failedTransactionCount = 0
  fmsOrders?.forEach((item: any, index: number) => {
    const transactionAmount = `\u20B9${numberWithCommas(item?.amount)}`
    item?.transactions?.forEach((transaction: any, transactionIndex: number) => {
      const isCODTransaction = transaction?.payment_mode === 'COD'

      if (transaction?.status === 'COMPLETED' && !isCODTransaction) {
        successfulTransaction.status = transaction?.Status
        successfulTransaction.payment_mode = mapPaymentMode(transaction?.payment_mode)
      }

      if (transaction?.status === 'FAILED') failedTransactionCount++
      const amountStr =
        transaction?.status === 'FAILED' || transaction?.status === 'CANCELLED' || isCODTransaction
          ? 'Amount payable'
          : 'Amount paid'
      transactionHistory?.push([
        {
          label: amountStr,
          value: transactionAmount
        },
        { label: 'Mode', value: mapPaymentMode(transaction?.payment_mode) ?? orderTableConstants.DASH },
        {
          label: 'Paid on',
          value: isCODTransaction
            ? orderTableConstants.DASH
            : `${getDateTime(new Date(transaction?.created_at))?.date} at ${
                getDateTime(new Date(transaction?.created_at))?.time
              }`
        },
        { label: 'Status', value: mapTransactionStatus(transaction?.status) },
        { label: 'Payment Gateway', value: mapGateWay(transaction?.pg_type) },
        { label: 'Payment ID', value: transaction?.pg_payment_id ?? orderTableConstants.DASH }
      ])
    })
  })
  checkoutInfo?.rewards
    ?.filter((reward: any) => reward?.type !== 'GC')
    ?.forEach((reward: any) => {
      const rewardLabel =
        reward?.type === 'WALLET'
          ? `${reward?.metadata?.points} Credits (₹${numberWithCommas(reward?.amount)})`
          : reward?.type === 'LOYALTY'
          ? `Loyalty (₹${numberWithCommas(reward?.amount)})`
          : `₹${numberWithCommas(reward?.amount)}`
      transactionHistory.push([
        { label: 'Amount paid', value: `₹${numberWithCommas(reward?.amount)}` },
        { label: 'Mode', value: 'Rewards' },
        { label: 'Reward credit used', value: rewardLabel },
        { label: 'Paid on', value: orderTableConstants.DASH },
        { label: 'Status', value: 'Successful' }
      ])
    })

  data?.gift_cards?.gc_transactions?.forEach((gc: any) => {
    const isFailedOrCancelled = gc?.status === 'FAILED' || gc?.status === 'CANCELLED'
    const paidOn = gc?.created_at
      ? `${getDateTime(new Date(gc.created_at))?.date} at ${getDateTime(new Date(gc.created_at))?.time}`
      : orderTableConstants.DASH

    const giftCardRow = [
      { label: isFailedOrCancelled ? 'Amount payable' : 'Amount paid', value: `₹${numberWithCommas(gc?.amount)}` },
      { label: 'Mode', value: 'Gift card' },
      { label: 'Paid on', value: paidOn },
      { label: 'Status', value: mapTransactionStatus(gc?.status) },
      { label: giftCardRowLabel.PROVIDER, value: mapGiftCardProvider(gc?.provider) }
    ]
    // masked_number is absent for providers that don't expose a card number (e.g. Shopify-native);
    // gc_transaction_id isn't populated in all environments yet, so fall back to transaction_id
    const giftCardId = gc?.gc_transaction_id ?? gc?.transaction_id
    if (gc?.masked_number) giftCardRow.push({ label: giftCardRowLabel.NUMBER, value: gc.masked_number })
    if (giftCardId) giftCardRow.push({ label: giftCardRowLabel.ID, value: giftCardId })

    transactionHistory.push(giftCardRow)
  })

  const refundTransaction = fmsOrders?.find((transactionData: any) => transactionData?.status === 'REFUNDED')
  const partialRefundTransaction = fmsOrders?.find(
    (transactionData: any) => transactionData?.status === 'PARTIALLY_REFUNDED'
  )
  const cancelledTransaction = fmsOrders?.find((transactionData: any) => transactionData?.status === 'CANCELLED')
  // TODO remove metadata removal and item name population from banckend

  const varaintDetails = lineItems?.map((item: any, index: number) => {
    return {
      itemID: item?.id,
      itemName: item?.product_title ?? '',
      itemPrice: `${(parseFloat(item?.price_after_discount) / 100).toString()}`,
      ...(Boolean(item?.price_after_discount !== item?.price)
        ? { strikedPrice: `${(parseFloat(item?.price) / 100).toString()}` }
        : {}),
      itemQuantity: item?.quantity,
      imageURL: item?.image,
      itemVariantName: item?.variant_title ?? '',
      productId: item?.product_id,
      originalQty: item?.quantity,
      isIncreasable: true,
      isDecreasable: true,
      showRestockCheckbox: false,
      isDiscountApplied: Boolean(item?.total_discount > 0),
      restock: true,
      discount: { type: 'PERCENTAGE', amount: '' }
    }
  })

  const getCODAmount = () => {
    const shippingCharge = checkoutInfo?.pricing?.shipping
    const codCharge = checkoutInfo?.pricing?.cod
    const totalCharge = shippingCharge + codCharge
    return `\u20B9${numberWithCommas(
      orderInfo?.order_type === 'COD' ? (totalCharge > 0 ? totalCharge : 0) : shippingCharge > 0 ? shippingCharge : 0
    )}`
  }

  const parsedShippingData = [
    {
      amount: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.shipping ?? 0)}`,
      label: checkoutInfo?.metadata?.shipping_lines?.[0]?.presentment_title
    }
  ]

  const parsedCodData = [
    {
      amount: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.cod ?? 0)}`,
      label: 'included'
    }
  ]

  const parsedRewardsData = checkoutInfo?.rewards?.map((item: any, index: number) => {
    return {
      type: item?.type,
      points: `${item?.metadata?.points}`,
      amount: `\u20B9${numberWithCommas(item?.amount)}`
    }
  })

  const parsedSubtotalData = [
    {
      items: lineItems?.length,
      amount: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.sub_total + (checkoutInfo?.pricing?.addon_total ?? 0))}`
    }
  ]

  const parsedTaxData = [
    {
      amount: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.tax ?? 0)}`,
      included: checkoutInfo?.pricing?.taxes_included
    }
  ]

  const parsedDiscountData = checkoutInfo?.discounts
    ?.filter((item: any) => item?.discount_code !== 'CHECKOUT_REWARDS')
    ?.map((item: any, index: number) => {
      return {
        code: item?.coupon_metadata?.title ?? item?.discount_code,
        amount: `- \u20B9${numberWithCommas(item?.discount)}`,
        label: item?.source
      }
    })

  const parsedSettlements = settlementInfo?.map((item: any, index: number) => {
    return [
      {
        label: 'Date',
        value: Boolean(item?.metadata)
          ? typeof item?.metadata?.settled_at === 'number'
            ? `${getDateTime(new Date(item?.metadata?.settled_at * 1000))?.date} at ${
                getDateTime(new Date(item?.metadata?.settled_at * 1000))?.time
              }`
            : `${getDateTime(new Date(item?.metadata?.settled_at))?.date} at ${
                getDateTime(new Date(item?.metadata?.settled_at))?.time
              }`
          : orderTableConstants.NOT_APPLICABLE
      },
      { label: 'Status', value: Boolean(item?.pg_reference_id) ? 'Settled' : orderTableConstants.NOT_APPLICABLE },
      {
        label: 'UTR',
        value: Boolean(item?.bank_reference_id) ? item?.bank_reference_id : orderTableConstants.NOT_APPLICABLE
      },
      {
        label: item?.context === 'REFUND' ? 'Refund ID' : 'Settlement ID',
        value: Boolean(item?.pg_reference_id) ? item?.pg_reference_id : orderTableConstants.NOT_APPLICABLE
      },
      { label: 'Settlement type', value: Boolean(item?.context) ? item?.context : orderTableConstants.NOT_APPLICABLE }
    ]
  })

  const parsedRefunds = refundInfo?.map((item: any) => {
    const refundedOn = `${getDateTime(new Date(item?.created_at))?.date} at ${
      getDateTime(new Date(item?.created_at))?.time
    }`
    const refundStatus = item?.status === 'COMPLETED' ? 'Successful' : item?.status ?? orderTableConstants.DASH

    return [
      { label: 'Refund Status', value: refundStatus },
      { label: 'Refunded on', value: refundedOn },
      { label: 'Refund amount', value: `\u20B9${numberWithCommas(item?.amount)}` },
      { label: 'Refund ID', value: item?.pg_reference_id },
      { label: 'Refund type', value: item?.refund_type }
    ]
  })

  const getAddress = (address: any) => {
    return [
      {
        name: `${address?.name ?? ''}`,
        addressLine1: [address?.address1, address?.address2].filter(Boolean).join(' '),
        addressLine2: `${address?.city} - ${address?.zip}`,
        email: `${address?.email}`,
        phone: `${address?.phone ?? ''}`
      }
    ]
  }

  const getBillingAddress = (address: any) => {
    return [
      {
        name: `${address?.name ?? ''}`,
        addressLine1: [address?.address1, address?.address2].filter(Boolean).join(' '),
        addressLine2: `${address?.city} - ${address?.zip}`,
        email: `${address?.email ?? orderTableConstants.DASH}`,
        phone: `${address?.phone ?? orderTableConstants.DASH}`
      }
    ]
  }

  const getPaymentStatus = () => {
    const status =
      successfulTransaction?.status ??
      refundTransaction?.status ??
      partialRefundTransaction?.status ??
      cancelledTransaction?.status
    switch (status) {
      case 'PARTIALLY_REFUNDED':
        return 'Partially refunded'
      case 'REFUNDED':
        return 'Refunded'
      case 'COMPLETED':
        return 'Completed'
      case 'CANCELLED':
        return 'Cancelled'
      default:
        return orderTableConstants.NOT_APPLICABLE
    }
  }

  const getUTMDetails = (data: any) => {
    const excludedKeys = new Set([
      'GSTIN',
      'GSTINBusinessName',
      'shopflo_checkout_url',
      'checkout_id',
      'checkout_token',
      'long_session_id',
      'gateway',
      'c2p_link',
      'c2p_expiry'
    ])
    const utmDataArray = data
      ?.filter((filterData: any) => !excludedKeys.has(filterData?.name))
      ?.map((utmData: any) => {
        return {
          label: utmData?.name,
          value: Boolean(utmData?.value?.length) ? utmData?.value : orderTableConstants.DASH
        }
      })
    return utmDataArray
  }

  const getGSTIN = (data: any) => {
    return data?.find((item: any) => item?.name === 'CustomerGSTIN')?.value ?? orderTableConstants.DASH
  }
  const getBusinessName = (data: any) => {
    return data?.find((item: any) => item?.name === 'GSTINBusinessName')?.value ?? orderTableConstants.DASH
  }

  return {
    status: checkoutInfo?.status ?? orderTableConstants.DASH,
    orderId: platformOrder?.data?.name ?? orderTableConstants.DASH,
    dateCreated: `${getDateTime(new Date(checkoutInfo?.created_at))?.date} at ${
      getDateTime(new Date(checkoutInfo?.created_at))?.time
    }`,
    customerName: `${data?.customer_details?.name ?? ''}`,
    contactDetails: formatContactDetails(data?.customer_details?.email, data?.customer_details?.phone),
    email: data?.customer_details?.email ?? orderTableConstants.DASH,
    phone: data?.customer_details?.phone ?? orderTableConstants.DASH,
    address: getAddress(data?.addresses?.shipping_address),
    paymentMode:
      orderInfo?.order_type === 'COD'
        ? orderTableConstants.PAYMENT_MODE_COD
        : orderInfo?.order_type === 'SPLIT_COD'
        ? orderTableConstants.PAYMENT_MODE_SPLIT_COD
        : orderTableConstants.PAYMENT_MODE_PREPAID,
    additionalCharge: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.additional_charges ?? 0)}`,
    convenienceFee: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.convenience_fee ?? 0)}`,
    codCharges: parsedCodData ?? orderTableConstants.DASH,
    subtotal: parsedSubtotalData?.length > 0 ? parsedSubtotalData : orderTableConstants.DASH,
    totalAmountPaid: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.total_payable)}`,
    totalPrice: `\u20B9${numberWithCommas(orderInfo?.amount)}`,
    editedOrderTotal: `\u20B9${numberWithCommas(
      calculateOrderTotal(orderInfo, platformOrder?.data, fmsOrders).toString()
    )}`,
    ...(orderInfo?.order_type === 'COD' ? { cod: `\u20B9${numberWithCommas(checkoutInfo?.pricing?.cod)}` } : {}),
    taxes: parsedTaxData?.length > 0 ? parsedTaxData : orderTableConstants.DASH,
    shipping: parsedShippingData ?? orderTableConstants.DASH,
    orderStatus: orderInfo?.status,
    paymentStatus:
      orderInfo?.order_type === 'COD'
        ? orderTableConstants.NOT_APPLICABLE
        : getPaymentStatus() ?? orderTableConstants.NOT_APPLICABLE,
    refunds: parsedRefunds ?? orderTableConstants.NOT_APPLICABLE,
    variantDetails: varaintDetails ?? orderTableConstants.NOT_APPLICABLE,
    transactionHistory: transactionHistory?.length ? transactionHistory : orderTableConstants.DASH,
    failedTransactionCount: failedTransactionCount.toString(),
    settlements: parsedSettlements ?? orderTableConstants.NOT_APPLICABLE,
    rewards: parsedRewardsData?.length > 0 ? parsedRewardsData : orderTableConstants.DASH,
    discounts: parsedDiscountData?.length > 0 ? parsedDiscountData : orderTableConstants.DASH,
    outStandingAmount: platformOrder?.data?.total_outstanding,
    utmDetails: getUTMDetails(checkoutInfo?.metadata?.note_attributes),
    c2pLink: c2pLink ?? null,
    GSTIN: getGSTIN(platformOrder?.data?.note_attributes),
    businessName: getBusinessName(platformOrder?.data?.note_attributes),
    billingAddress: getBillingAddress(data?.addresses?.billing_address)
  }
}

export const localDeliveryGroupAdapter = (data: any) => {
  return {
    groupID: data?.group_id,
    groupName: data?.name,
    etdText: data?.etd_text,
    isActive: data?.enabled,
    charges:
      data?.delivery_pricing_conditions?.length > 1
        ? 'Conditional'
        : data?.delivery_pricing_conditions?.[0]?.delivery_price ?? 0
  }
}

export const salesByTableAdapter = (data: any) => {
  const resultingRow: any = {}
  data?.forEach((columnValue: any, index: number) => {
    switch (index) {
      case 0:
        resultingRow['salesBy'] = columnValue
      case 1:
        resultingRow['revenue'] = roundToDecimals(columnValue)
      case 2:
        resultingRow['ordersCount'] = columnValue
    }
  })
  resultingRow['id'] = Math.random()

  return resultingRow
}

export const funnelAdapter = (data: any) => {
  const resultingRow: any = {}
  data?.forEach((columnValue: any, index: number) => {
    switch (index) {
      case 0:
        resultingRow['eventName'] = columnValue
      case 1:
        resultingRow['sessions'] = columnValue
      case 2:
        resultingRow['percentage'] = columnValue
    }
  })
  resultingRow['id'] = Math.random()

  return resultingRow
}

export const conversionReportAdapter = (data: any) => {
  const resultingRow: any = { ...data }
  return resultingRow
}

export const discountFailureRateAdapter = (data: any) => {
  const resultingRow: any = {}
  data?.forEach((columnValue: any, index: number) => {
    switch (index) {
      case 0:
        resultingRow['code'] = columnValue
      case 1:
        resultingRow['discounts_selected'] = columnValue
      case 2:
        resultingRow['codes_entered'] = columnValue
      case 3:
        resultingRow['failed_attempts'] = columnValue
      case 4:
        resultingRow['failure_rate'] = columnValue
    }
  })
  resultingRow['id'] = Math.random()

  return resultingRow
}

export const discountConversionsAdapter = (data: any) => {
  const resultingRow: any = {}
  data?.forEach((columnValue: any, index: number) => {
    switch (index) {
      case 0:
        resultingRow['code'] = columnValue
      case 1:
        resultingRow['checkouts'] = columnValue
      case 2:
        resultingRow['orders'] = columnValue
      case 3:
        resultingRow['conversions'] = columnValue
    }
  })
  resultingRow['id'] = Math.random()

  return resultingRow
}

export const customerTransactionDataAdapter = (data: any) => {
  const parsedData = data?.map((row: any) => {
    const { date, time } = getDateTime(row?.checkout_details?.created_at)
    return {
      id: row?.transaction_id,
      createdAt: `${getDateTime(row?.created_on)?.date} at ${getDateTime(row?.created_on)?.time}`,
      status: row?.status,
      transactionAmount: `\u20B9 ${numberWithCommas(row?.transaction_amount)}`,
      transactionType: row?.transaction_type,
      transactionId: row?.transaction_id,
      userId: row?.user_id,
      walletId: row?.wallet_id,
      reason: row?.reason ?? '',
      reference: row?.reference ?? '',
      checkoutDetails: {
        orderType: row?.checkout_details?.order_type,
        platform: row?.checkout_details?.platform,
        orderId: row?.checkout_details?.platform_order_id,
        checkoutId: row?.checkout_details?.checkout_id,
        orderAmount: `\u20B9 ${numberWithCommas(row?.checkout_details?.amount)}`,
        orderCreatedAt: Boolean(date) && Boolean(time) ? `${date} at ${time}` : orderTableConstants.NOT_APPLICABLE
      }
    }
  })
  return parsedData
}

//need to update the adapter
export const paymentSplitReportAdapter = (data: any) => {
  const resultingRow = data
  resultingRow['id'] = Math.random()
  return resultingRow
}

export const channelListAdapter = (data: any) => {
  const resultingRow = {
    channelName: data?.name,
    id: data?.id
  }
  return resultingRow
}

export const upSellAdapter = (row: any): UpSellTableDataType => {
  const parsedEntitlements = {
    products: [] as UpsellEntitledProductType[],
    collections: [] as UpsellEntitledCollectionType[]
  }

  row?.entitled_products?.forEach((entitled_product: any) => {
    if (entitled_product?.product_id) {
      parsedEntitlements.products.push({
        productId: entitled_product?.product_id,
        productDiscount: {
          concessionAmount: entitled_product?.discount?.concession_amount,
          type: entitled_product?.discount?.type
        }
      })
    } else if (entitled_product?.collection_id) {
      parsedEntitlements.collections.push({
        collectionId: entitled_product?.collection_id,
        collectionDiscount: {
          concessionAmount: entitled_product?.discount?.concession_amount,
          type: entitled_product?.discount?.type
        }
      })
    }
  })

  return {
    id: row?.id,
    name: row?.name,
    entitledProductIds: row?.entitled_product_ids,
    entitledProducts: parsedEntitlements.products,
    entitledCollections: parsedEntitlements.collections,
    prerequisiteProductIds: row?.prerequisite_product_ids,
    prerequisiteVariantIds: row?.prerequisite_variant_ids,
    prerequisiteCollectionIds: row?.prerequisite_collection_ids,
    viewCount: row?.view_count,
    appliedCount: row?.applied_count,
    gmvProcessed: row?.gmv_processed,
    isActive: row?.is_active,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
    activeFrom: row?.active_from,
    activeUntil: row?.active_until,
    metadata: row?.metadata
  }
}

export const onboardingV2Adapter = (data: OnboardingStateType[]): OnboardingStepType[] => {
  const parsedData = data
    ?.filter((item: any) => !item?.parent_id)
    ?.map((item: any) => {
      return {
        id: item?.id,
        mandatory: item?.mandatory
      }
    })

  return parsedData ?? []
}

export const SubscriptionV2Adapter = (data: any): SubscriptionV2Type[] => {
  const parsedData = data?.map((item: any) => {
    return {
      id: item?.plan_id,
      realEstate: item?.real_estate
    }
  })

  return parsedData ?? []
}
